import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Box,
    Paper,
    Button,
    Autocomplete,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RadioactiveMaterial, radioactiveMaterials } from './types/radiation';
import axios from 'axios';
import { SelectChangeEvent } from '@mui/material/Select';

// 파란색 마커 아이콘도 명시적으로 생성
const blueIcon = new L.Icon({
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// 빨간색 마커 아이콘 제거 (더 이상 사용하지 않음)

// 지도 위치 변경 컴포넌트
function ChangeView({ center }: { center: [number, number] }) {
    const map = useMap();
    map.setView(center, map.getZoom());
    return null;
}

interface WindInfo {
    풍속: string;
    풍향: string;
}

interface DataItem {
    id: number;
    물질: string;
    물질량: string;
    unit: string;
    주소: string;
    위치이미지: string;
    바람정보: WindInfo;
    weather?: WeatherData | null;
    zoneInfo?: any | null;
    lat?: number;
    lon?: number;
}

interface AddressResult {
    display_name: string;
    lat: string;
    lon: string;
}

interface EvacuationZone {
    radius: number; // meters
    color: string;
    label: string;
    doseRateThreshold: number; // μSv/h
}

interface WeatherData {
    temp: number; // 온도 (°C)
    humidity: number; // %
    windSpeed: number; // m/s
    windDirection: number; // degrees
    description?: string;
    cloudiness: number; // %
    stabilityClass?: string; // 추정 또는 가정된 대기 안정도
}

// userPoints 상태의 타입을 명확하게 정의
interface UserPoint {
    id: number;
    position: [number, number];
    label: string;
    type: 'address' | 'custom';
    name?: string;
    address?: string;
    zones?: EvacuationZone[] | null;
    zoneInfo?: any | null;
}

function App() {
    const [selectedMaterial, setSelectedMaterial] = useState<RadioactiveMaterial | null>(null);
    const [amount, setAmount] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [dataList, setDataList] = useState<DataItem[]>([]);
    const [selectedUnit, setSelectedUnit] = useState<string>('Bq');
    const [mapCenter, setMapCenter] = useState<[number, number]>([37.5665, 126.9780]);
    const [markers, setMarkers] = useState<Array<{ position: [number, number], name: string }>>([]);
    const [searchResults, setSearchResults] = useState<AddressResult[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [evacuationZones, setEvacuationZones] = useState<EvacuationZone[]>([]);
    const [calculatedZoneInfo, setCalculatedZoneInfo] = useState<{
        hot: { radius: number, label: string } | null;
        warm: { radius: number, label: string } | null;
        cold: { radius: number, label: string } | null;
        fragment?: { radius: number, label: string } | null;
    } | null>(null);
    const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
    const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
    const [expandedMaterialInfo, setExpandedMaterialInfo] = useState(true); // 펼쳐짐
    const [expandedWeatherInfo, setExpandedWeatherInfo] = useState(false); // 접힘
    const [userPoints, setUserPoints] = useState<UserPoint[]>([]);
    const [isAddingCustomPoint, setIsAddingCustomPoint] = useState<boolean>(false);
    const [newCustomPointLocation, setNewCustomPointLocation] = useState<[number, number] | null>(null);
    const [newCustomPointName, setNewCustomPointName] = useState('');
    const [newCustomPointType, setNewCustomPointType] = useState<string>('지휘소');
    const [isExplosion, setIsExplosion] = useState(false);
    const [centerAddress, setCenterAddress] = useState<string>('');
    const shareRef = React.useRef<HTMLDivElement>(null);
    const [isMovingCenter, setIsMovingCenter] = useState(false); // 중심점 이동 기능 복원

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    // 방사능 물질량 단위 변환 함수 (Bq로 통일)
    const convertAmountToBq = useCallback((amountValue: string, unit: string, material: RadioactiveMaterial | null): number => {
        if (!material) return 0;
        const numericAmount = parseFloat(amountValue);
        if (isNaN(numericAmount)) return 0;

        switch (unit) {
            case 'Bq': return numericAmount;
            case 'MBq': return numericAmount * 1e6;
            case 'GBq': return numericAmount * 1e9;
            case 'Ci': return numericAmount * 3.7e10; // 1 Ci = 3.7 x 10^10 Bq
            default: return 0;
        }
    }, []); // 이 함수는 외부 상태에 의존하지 않으므로 빈 배열

    // 1. 상태 추가
    // 중심점 마커의 주소 상태 추가
    // 대피 구역 계산 로직
    const calculateEvacuationZones = useCallback((material: RadioactiveMaterial, amountInBq: number, center: [number, number]) => {
        if (material.physicalForm.includes('액체')) {
            // 가우시안 플룸 모델 (더미)
            return calculateGaussianPlumeEvacuation(material, amountInBq, center, weatherData);
        } else if (material.physicalForm.includes('고체')) {
            if (isExplosion) {
                // 파편/분산 모델 (더미)
                return calculateFragmentDispersionEvacuation(material, amountInBq, center);
            } else {
                // 근접 노출(C) 모델 (기존)
                return calculateProximityExposureEvacuation(material, amountInBq, center);
            }
        }
        // 기타는 기존 방식
        return { zoneInfo: null };
    }, [isExplosion, weatherData]);

    useEffect(() => {
        if (selectedMaterial && amount && selectedUnit && mapCenter) {
            const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial);
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, mapCenter);
            setCalculatedZoneInfo(zoneInfo);

            // zoneInfo를 EvacuationZone[] 형태로 변환 (Cold → Warm → Hot → Fragment 순서)
            if (zoneInfo) {
                const newZones: EvacuationZone[] = [];
                if (zoneInfo.cold) newZones.push({ ...zoneInfo.cold, color: 'green', label: 'Cold Zone', doseRateThreshold: 0.001 });
                if (zoneInfo.warm) newZones.push({ ...zoneInfo.warm, color: 'orange', label: 'Warm Zone', doseRateThreshold: 0.2 });
                if (zoneInfo.hot) newZones.push({ ...zoneInfo.hot, color: 'red', label: 'Hot Zone', doseRateThreshold: 20 });
                if (zoneInfo.fragment) newZones.push({ ...zoneInfo.fragment, color: '#FF9800', label: 'Fragment Zone', doseRateThreshold: 0 });
                setEvacuationZones(newZones);
            } else {
                setEvacuationZones([]);
            }
        } else {
            setEvacuationZones([]);
            setCalculatedZoneInfo(null);
        }
    }, [selectedMaterial, amount, selectedUnit, mapCenter, convertAmountToBq, calculateEvacuationZones]);

    const handleMaterialChange = (event: any) => {
        const material = radioactiveMaterials.find(m => m.id === event.target.value);
        setSelectedMaterial(material || null);
        // 물질, 양, 단위, 주소, 날씨 정보가 모두 있을 때만 구역 계산
        if (material && amount && selectedUnit && selectedAddress && weatherData) {
            const amountInBq = convertAmountToBq(amount, selectedUnit, material);
            const { zoneInfo } = calculateEvacuationZones(material, amountInBq, [parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lon)]);
            setEvacuationZones([]); // zones 배열 초기화
            setCalculatedZoneInfo(zoneInfo);
        } else {
            setEvacuationZones([]);
            setCalculatedZoneInfo(null);
            setUserPoints([]); // 물질 변경 시 기존 사용자 지점 초기화 (필요시 조정)
        }
        setAddress('');
        setWeatherData(null);
        setSearchResults([]);
        setSelectedAddress(null);
        setExpandedMaterialInfo(false);
    };

    const handleAddData = () => {
        if (!selectedMaterial || !amount || !centerAddress || !calculatedZoneInfo) return;
        const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial);
        const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, mapCenter);
        setDataList([
            ...dataList,
            {
                id: Date.now(),
                물질: selectedMaterial.name,
                물질량: amount,
                unit: selectedUnit,
                주소: centerAddress,
                위치이미지: 'https://via.placeholder.com/200x100?text=지도',
                바람정보: { 풍속: '', 풍향: '', },
                weather: weatherData,
                zoneInfo: zoneInfo,
                lat: mapCenter[0],
                lon: mapCenter[1],
            }
        ]);
        setAmount('');
        setAddress('');
    };

    // 현재 위치 가져오기 함수
    const handleCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setMapCenter([latitude, longitude]);
                    setMarkers([{ position: [latitude, longitude], name: '현재 위치' }]);
                    
                    // 현재 위치를 userPoints에도 추가, type을 'address'로 지정
                    setUserPoints(prevPoints => [...prevPoints, {
                        id: Date.now(), // 고유 ID
                        position: [latitude, longitude],
                        label: '현재 위치',
                        type: 'address',
                        zones: null, // 초기에는 구역 정보 없음
                        zoneInfo: null,
                    }]);

                    // 현재 위치의 주소 가져오기
                    const address = await fetchAddressFromCoords(latitude, longitude);
                    setCenterAddress(address);
                    
                    // 현재 위치를 selectedAddress로 설정 (가상의 주소 객체 생성)
                    const currentAddress = {
                        display_name: address,
                        lat: latitude.toString(),
                        lon: longitude.toString()
                    };
                    setSelectedAddress(currentAddress);
                    setAddress(address);

                    // 현재 위치의 날씨 정보 자동으로 가져오기
                    fetchWeatherData(latitude, longitude);

                    // 방사성 물질과 양이 선택되어 있다면 대피구역 계산
                    if (selectedMaterial && amount && selectedUnit) {
                        const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial);
                        const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [latitude, longitude]);
                        setEvacuationZones([]); // zones 배열 초기화
                        setCalculatedZoneInfo(zoneInfo);
                    }

                    // 아코디언 자동 펼침
                    setExpandedMaterialInfo(true);
                },
                (error) => {
                    alert('위치 정보를 가져오는데 실패했습니다.');
                    console.error('Geolocation error:', error);
                }
            );
        } else {
            alert('이 브라우저에서는 위치 정보를 지원하지 않습니다.');
        }
    };

    // 주소 검색 함수 (대체 API 포함)
    const handleSearchAddress = async () => {
        if (!address) {
            alert('주소를 입력해주세요.');
            return;
        }

        setIsSearching(true);
        try {
            // 첫 번째 시도: OpenStreetMap API
            let response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=kr&limit=5`,
                {
                    headers: {
                        'Accept-Language': 'ko',
                        'User-Agent': 'YourAppName/1.0'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const results = await response.json();
            setSearchResults(results);

            if (results.length === 1) {
                // 결과가 하나면 바로 선택
                handleAddressSelect(results[0]);
            } else if (results.length === 0) {
                alert('주소를 찾을 수 없습니다.');
            }
        } catch (error) {
            console.error('OpenStreetMap API 실패, 대체 API 시도:', error);
            
            try {
                // 두 번째 시도: 타임아웃을 늘려서 재시도
                console.log('OpenStreetMap API 재시도 중...');
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃
                
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=kr&limit=5`,
                    {
                        headers: {
                            'Accept-Language': 'ko',
                            'User-Agent': 'YourAppName/1.0'
                        },
                        signal: controller.signal
                    }
                );

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const results = await response.json();
                setSearchResults(results);

                if (results.length === 1) {
                    handleAddressSelect(results[0]);
                } else if (results.length === 0) {
                    alert('주소를 찾을 수 없습니다.');
                }
            } catch (fallbackError) {
                console.error('재시도도 실패:', fallbackError);
                alert('주소 검색 서비스에 일시적인 문제가 있습니다. 잠시 후 다시 시도해주세요.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddressSelect = (address: AddressResult) => {
        setSelectedAddress(address);
        setAddress(address.display_name);
        setMapCenter([parseFloat(address.lat), parseFloat(address.lon)]);
        setMarkers([{ position: [parseFloat(address.lat), parseFloat(address.lon)], name: address.display_name }]);
        setSearchResults([]); // 드롭다운 닫기
        setCenterAddress(address.display_name); // 주소 상태 저장
        // 선택된 주소를 userPoints에도 추가, type을 'address'로 지정
        setUserPoints(prevPoints => [...prevPoints, {
            id: Date.now(), // 고유 ID
            position: [parseFloat(address.lat), parseFloat(address.lon)],
            label: address.display_name,
            type: 'address',
            zones: null, // 초기에는 구역 정보 없음
            zoneInfo: null,
        }]);

        // 항상 날씨 정보를 가져오도록 변경
        fetchWeatherData(parseFloat(address.lat), parseFloat(address.lon));

        if (selectedMaterial && amount && selectedUnit) { // selectedUnit 조건 추가
            const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial); // Bq로 변환
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [parseFloat(address.lat), parseFloat(address.lon)]);
            setEvacuationZones([]); // zones 배열 초기화
            setCalculatedZoneInfo(zoneInfo);
        }
        setExpandedMaterialInfo(true); // 위치 검색 후 아코디언 자동 펼침
    };

    // 지도 클릭 시 사용자 지정 지점 추가 모드일 때 동작
    const handleMapClickForCustomPoint = (latlng: L.LatLng) => {
        setNewCustomPointLocation([latlng.lat, latlng.lng]);
        // 다이얼로그는 isAddingCustomPoint && newCustomPointLocation !== null 일 때 열리므로 추가 로직 불필요
    };

    // 엔터 키 입력 시 주소 검색
    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            handleSearchAddress();
        }
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newAmount = event.target.value;
        setAmount(newAmount);
        // 양이 변경될 때만 구역을 다시 계산
        if (selectedMaterial && newAmount && selectedUnit && selectedAddress && weatherData) { // selectedUnit 조건 추가
            const amountInBq = convertAmountToBq(newAmount, selectedUnit, selectedMaterial); // Bq로 변환
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lon)]);
            setEvacuationZones([]); // zones 배열 초기화
            setCalculatedZoneInfo(zoneInfo);
        } else {
            setEvacuationZones([]);
            setCalculatedZoneInfo(null);
        }
    };

    const handleUnitChange = (event: SelectChangeEvent<string>) => {
        const newUnit = event.target.value;
        setSelectedUnit(newUnit);
        // 단위가 변경될 때만 구역을 다시 계산
        if (selectedMaterial && amount && newUnit && selectedAddress && weatherData) { // newUnit 조건 추가
            const amountInBq = convertAmountToBq(amount, newUnit, selectedMaterial); // Bq로 변환
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lon)]);
            setEvacuationZones([]); // zones 배열 초기화
            setCalculatedZoneInfo(zoneInfo);
        } else {
            setEvacuationZones([]);
            setCalculatedZoneInfo(null);
        }
    };

    const handleAccordionChange = (panelId: number) => () => {
        setExpandedItemId(expandedItemId === panelId ? null : panelId);
    };

    // 날씨 정보를 가져오는 함수 (Tomorrow.io API 사용)
    const fetchWeatherData = async (lat: number, lon: number) => {
        const API_KEY = process.env.REACT_APP_WEATHER_API_KEY || '5trNZ7tBNhvoW2J3zHLQ7dXgW2J3zHLQ7dXgWohWz55S';
        if (!API_KEY) {
            console.error('Tomorrow.io API Key가 설정되지 않았습니다.');
            return;
        }

        try {
            console.log(`날씨 정보 요청: https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&units=metric&apikey=${API_KEY}`);
            const response = await axios.get(`https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&units=metric&apikey=${API_KEY}`);
            console.log('Tomorrow.io API Response:', response.data);
            const data = response.data.data.values;
            const weatherCode = data.weatherCode;

            // 대기 안정도 추정 (임의 기준)
            let stabilityClass = 'D (중립)'; // 기본값
            if (data.windSpeed > 6) {
                stabilityClass = 'C (약간 불안정)'; // 강풍
            } else if (data.cloudCoverAvg < 30 && (data.temperature > 25 || data.temperature < 0)) {
                stabilityClass = 'B (불안정)'; // 맑고 더움/추움
            } else if (data.cloudCoverAvg > 70) {
                stabilityClass = 'F (매우 안정)'; // 흐림
            }

            setWeatherData({
                temp: data.temperature,
                humidity: data.humidity,
                windSpeed: data.windSpeed,
                windDirection: data.windDirection,
                description: getWeatherDescription(weatherCode),
                cloudiness: data.cloudCover,
                stabilityClass: stabilityClass, // 대기 안정도 추가
            });
        } catch (error) {
            console.error('날씨 정보를 가져오는데 실패했습니다:', error);
            setWeatherData(null);
        }
    };

    const handleMaterialInfoAccordionChange = () => {
        setExpandedMaterialInfo(!expandedMaterialInfo);
    };

    const handleWeatherInfoAccordionChange = () => {
        setExpandedWeatherInfo(!expandedWeatherInfo);
    };

    // 지도 클릭 이벤트 핸들러 컴포넌트
    function MapClickHandler({ onMapClickForCustomPoint, isAddingCustomPointMode, onCenterMove, isMovingCenterMode, onMoveCenter }: { onMapClickForCustomPoint: (latlng: L.LatLng) => void, isAddingCustomPointMode: boolean, onCenterMove: (latlng: L.LatLng, originalEvent: any) => void, isMovingCenterMode: boolean, onMoveCenter: (latlng: L.LatLng) => void }) {
        useMapEvents({
            click: (e) => {
                if (isAddingCustomPointMode) {
                    onMapClickForCustomPoint(e.latlng);
                } else if (isMovingCenterMode) {
                    onMoveCenter(e.latlng);
                } else if (e.originalEvent && e.originalEvent.ctrlKey) {
                    onCenterMove(e.latlng, e.originalEvent);
                }
            },
        });
        return null;
    }

    // 마커 드래그 이벤트 핸들러 추가
    const handleMarkerDragEnd = async (pointId: number, newPosition: L.LatLng) => {
        // reverse geocoding으로 새 주소 가져오기
        const newAddress = await fetchAddressFromCoords(newPosition.lat, newPosition.lng);
        setUserPoints(prevPoints =>
            prevPoints.map(point =>
                point.id === pointId
                    ? { ...point, position: [newPosition.lat, newPosition.lng], address: newAddress }
                    : point
            )
        );
    };

    // 마커 삭제 핸들러 추가
    const handleDeletePoint = (pointId: number) => {
        setUserPoints(prevPoints => prevPoints.filter(point => point.id !== pointId));
    };

    // OpenWeatherMap 날씨 코드에 따른 설명
    const getWeatherDescription = (weatherCode: number): string => {
        switch (weatherCode) {
            case 0: return '알 수 없음';
            case 1000: return '맑음';
            case 1100: return '대체로 맑음';
            case 1101: return '부분적으로 흐림';
            case 1102: return '대체로 흐림';
            case 1001: return '흐림';
            case 2000: return '안개';
            case 2100: return '옅은 안개';
            case 4000: return '이슬비';
            case 4001: return '비';
            case 4200: return '약한 비';
            case 4201: return '폭우';
            case 5000: return '눈';
            case 5001: return '가벼운 눈';
            case 5100: return '약한 눈';
            case 5101: return '폭설';
            case 6000: return '진눈깨비';
            case 6001: return '얼어붙는 비';
            case 6200: return '약한 얼어붙는 비';
            case 6201: return '강한 얼어붙는 비';
            case 7000: return '싸라기눈';
            case 7101: return '강한 싸라기눈';
            case 7102: return '약한 싸라기눈';
            case 8000: return '뇌우';
            default: return '알 수 없음';
        }
    };

    // 풍향을 텍스트로 변환하는 함수
    const getWindDirectionText = (degree: number): string => {
        if (degree >= 337.5 || degree < 22.5) return '북';
        if (degree >= 22.5 && degree < 67.5) return '북동';
        if (degree >= 67.5 && degree < 112.5) return '동';
        if (degree >= 112.5 && degree < 157.5) return '남동';
        if (degree >= 157.5 && degree < 202.5) return '남';
        if (degree >= 202.5 && degree < 247.5) return '남서';
        if (degree >= 247.5 && degree < 292.5) return '서';
        if (degree >= 292.5 && degree < 337.5) return '북서';
        return '알 수 없음';
    };

    // 4. 각 모델별 더미 함수 추가 (실제 계산식은 추후 구현)
    function calculateGaussianPlumeEvacuation(material: RadioactiveMaterial, amountInBq: number, center: [number, number], weatherData: any) {
        if (!weatherData) {
            // 날씨 정보가 없으면 더미 값 반환
            return { zoneInfo: { plume: { radius: 500, label: 'Plume Zone' } } };
        }

        // 가우시안 플룸 모델 계산
        const Q = amountInBq; // 방출률 (Bq/s) - 단순화를 위해 총량으로 가정
        const u = weatherData.windSpeed; // 풍속 (m/s)
        
        // 대기안정도에 따른 확산 계수 결정
        let stabilityClass: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' = 'D'; // 기본값
        if (weatherData.stabilityClass) {
            const match = weatherData.stabilityClass.match(/^([A-F])/);
            if (match) {
                const classLetter = match[1] as 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
                if (['A', 'B', 'C', 'D', 'E', 'F'].includes(classLetter)) {
                    stabilityClass = classLetter;
                }
            }
        }
        
        // 대기안정도별 확산 계수 (Pasquill-Gifford 분류)
        const sigmaParams: Record<'A' | 'B' | 'C' | 'D' | 'E' | 'F', { ay: number; by: number; az: number; bz: number }> = {
            'A': { ay: 0.22, by: 0.78, az: 0.20, bz: 0.61 }, // 매우 불안정
            'B': { ay: 0.16, by: 0.78, az: 0.12, bz: 0.61 },
            'C': { ay: 0.11, by: 0.78, az: 0.08, bz: 0.61 }, // 약간 불안정
            'D': { ay: 0.08, by: 0.78, az: 0.06, bz: 0.61 }, // 중립
            'E': { ay: 0.06, by: 0.78, az: 0.04, bz: 0.61 },
            'F': { ay: 0.04, by: 0.78, az: 0.02, bz: 0.61 }  // 매우 안정
        };
        
        const params = sigmaParams[stabilityClass];
        
        // 선량변환계수 (DCF) - 감마선 기준 (μSv·m²/MBq·h)
        const DCF = material.gammaConstant;
        
        // 기준 선량률 (μSv/h)
        const hotZoneDoseRate = 20;   // Hot Zone
        const warmZoneDoseRate = 0.2; // Warm Zone
        const coldZoneDoseRate = 0.001; // Cold Zone
        
        // 거리별 농도 및 선량률 계산
        const calculateDoseRate = (distance: number): number => {
            if (distance <= 0 || u <= 0) return 0;
            
            // σy, σz 계산 (거리에 따른 확산 계수)
            const sigmaY = params.ay * Math.pow(distance, params.by);
            const sigmaZ = params.az * Math.pow(distance, params.bz);
            
            // 가우시안 플룸 농도 계산 (단순화된 버전)
            // C = Q / (2πσyσzu) × exp(-y²/2σy²) × exp(-(z-H)²/2σz²)
            // 여기서는 중심축(y=0, z=0)에서의 농도만 계산
            const concentration = Q / (2 * Math.PI * sigmaY * sigmaZ * u);
            
            // 선량률 계산: Ḋ = C × DCF / r²
            const doseRate = (concentration * DCF) / (distance * distance);
            
            return doseRate;
        };
        
        // 각 구역의 반경 계산
        const findRadiusForDoseRate = (targetDoseRate: number): number => {
            let radius = 1;
            let maxRadius = 10000; // 최대 10km
            let step = 10;
            
            while (radius <= maxRadius) {
                const doseRate = calculateDoseRate(radius);
                if (doseRate <= targetDoseRate) {
                    return radius;
                }
                radius += step;
            }
            return maxRadius;
        };
        
        const hotRadius = findRadiusForDoseRate(hotZoneDoseRate);
        const warmRadius = findRadiusForDoseRate(warmZoneDoseRate);
        const coldRadius = findRadiusForDoseRate(coldZoneDoseRate);
        
        console.log('가우시안 플룸 계산 결과:', {
            material: material.name,
            amountInBq,
            windSpeed: u,
            stabilityClass,
            hotRadius,
            warmRadius,
            coldRadius
        });
        
        // 구역 정보 생성
        const zoneInfo: any = { hot: null, warm: null, cold: null };
        
        if (hotRadius > 0 && hotRadius < 10000) {
            zoneInfo.hot = { radius: hotRadius, label: 'Hot Zone' };
        }
        if (warmRadius > 0 && warmRadius < 10000) {
            zoneInfo.warm = { radius: warmRadius, label: 'Warm Zone' };
        }
        if (coldRadius > 0 && coldRadius < 10000) {
            zoneInfo.cold = { radius: coldRadius, label: 'Cold Zone' };
        }
        
        console.log('최종 zoneInfo:', zoneInfo);
        return { zoneInfo };
    }
    function calculateFragmentDispersionEvacuation(material: RadioactiveMaterial, amountInBq: number, center: [number, number]) {
        // TODO: 실제 파편/분산 모델 계산식 적용
        return { zoneInfo: { fragment: { radius: 300, label: 'Fragment Zone' } } };
    }
    function calculateProximityExposureEvacuation(material: RadioactiveMaterial, amountInBq: number, center: [number, number]) {
        // 기존 근접 노출(C) 모델 (기존 calculateEvacuationZones 내용 복사)
        const hotZoneDoseRate = 20;
        const warmZoneDoseRate = 0.2;
        const gammaConstant = material.gammaConstant;
        const calculateRadius = (doseRate: number): number => {
            if (doseRate <= 0 || amountInBq <= 0) return Infinity;
            const activityMBq = amountInBq / 1e6;
            const radiusSquared = (gammaConstant * activityMBq) / doseRate;
            return Math.sqrt(radiusSquared);
        };
        const hotRadius = calculateRadius(hotZoneDoseRate);
        const warmRadius = calculateRadius(warmZoneDoseRate);
        let coldRadius: number;
        if (warmRadius <= 400) {
            coldRadius = 400;
        } else {
            coldRadius = warmRadius + 30;
        }
        const zoneInfo: any = { hot: null, warm: null, cold: null };
        if (!isNaN(coldRadius) && coldRadius > 0) {
            zoneInfo.cold = { radius: coldRadius, label: 'Cold Zone' };
        }
        if (!isNaN(warmRadius) && warmRadius > 0) {
            zoneInfo.warm = { radius: warmRadius, label: 'Warm Zone' };
        }
        if (!isNaN(hotRadius) && hotRadius > 0) {
            zoneInfo.hot = { radius: hotRadius, label: 'Hot Zone' };
        }
        if (hotRadius > warmRadius) {
            return { zoneInfo: {
                hot: { radius: hotRadius, label: 'Hot Zone' },
                warm: null,
                cold: null,
            } };
        }
        return { zoneInfo };
    }

    // 사용자 지정 지점 추가 시 reverse geocoding
    async function fetchAddressFromCoords(lat: number, lon: number): Promise<string> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000); // 8초 타임아웃
            
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=ko`,
                { signal: controller.signal }
            );
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data.display_name || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        } catch (error) {
            console.warn('Reverse geocoding 실패, 좌표 반환:', error);
            return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        }
    }

    // 중심점 이동 함수
    const handleCenterMoveByClick = async (latlng: L.LatLng, originalEvent: any) => {
        setMapCenter([latlng.lat, latlng.lng]);
        if (selectedAddress) {
            setSelectedAddress({
                ...selectedAddress,
                lat: latlng.lat.toString(),
                lon: latlng.lng.toString()
            });
        }
        const address = await fetchAddressFromCoords(latlng.lat, latlng.lng);
        setCenterAddress(address);
        // 중심점 마커는 항상 하나만 유지
        setMarkers([{ position: [latlng.lat, latlng.lng], name: address }]);
        // 대피구역 재계산
        if (selectedMaterial && amount && selectedUnit) {
            const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial);
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [latlng.lat, latlng.lng]);
            
            // zoneInfo를 EvacuationZone[] 형태로 변환하여 evacuationZones 상태 업데이트
            if (zoneInfo) {
                const newZones: EvacuationZone[] = [];
                if (zoneInfo.cold) newZones.push({ ...zoneInfo.cold, color: 'green', label: 'Cold Zone', doseRateThreshold: 0.001 });
                if (zoneInfo.warm) newZones.push({ ...zoneInfo.warm, color: 'orange', label: 'Warm Zone', doseRateThreshold: 0.2 });
                if (zoneInfo.hot) newZones.push({ ...zoneInfo.hot, color: 'red', label: 'Hot Zone', doseRateThreshold: 20 });
                if (zoneInfo.fragment) newZones.push({ ...zoneInfo.fragment, color: '#FF9800', label: 'Fragment Zone', doseRateThreshold: 0 });
                setEvacuationZones(newZones);
            } else {
                setEvacuationZones([]);
            }
            
            setCalculatedZoneInfo(zoneInfo);
        }
    };

    // 중심점 이동 함수
    const handleMoveCenter = async (latlng: L.LatLng) => {
        console.log('중심점 이동 시작:', latlng.lat, latlng.lng);
        setMapCenter([latlng.lat, latlng.lng]);
        if (selectedAddress) {
            setSelectedAddress({
                ...selectedAddress,
                lat: latlng.lat.toString(),
                lon: latlng.lng.toString()
            });
        }
        const address = await fetchAddressFromCoords(latlng.lat, latlng.lng);
        setCenterAddress(address);
        setMarkers([{ position: [latlng.lat, latlng.lng], name: address }]);
        // 대피구역 재계산
        if (selectedMaterial && amount && selectedUnit) {
            const amountInBq = convertAmountToBq(amount, selectedUnit, selectedMaterial);
            const { zoneInfo } = calculateEvacuationZones(selectedMaterial, amountInBq, [latlng.lat, latlng.lng]);
            
            // zoneInfo를 EvacuationZone[] 형태로 변환하여 evacuationZones 상태 업데이트
            if (zoneInfo) {
                const newZones: EvacuationZone[] = [];
                if (zoneInfo.cold) newZones.push({ ...zoneInfo.cold, color: 'green', label: 'Cold Zone', doseRateThreshold: 0.001 });
                if (zoneInfo.warm) newZones.push({ ...zoneInfo.warm, color: 'orange', label: 'Warm Zone', doseRateThreshold: 0.2 });
                if (zoneInfo.hot) newZones.push({ ...zoneInfo.hot, color: 'red', label: 'Hot Zone', doseRateThreshold: 20 });
                if (zoneInfo.fragment) newZones.push({ ...zoneInfo.fragment, color: '#FF9800', label: 'Fragment Zone', doseRateThreshold: 0 });
                setEvacuationZones(newZones);
            } else {
                setEvacuationZones([]);
            }
            
            setCalculatedZoneInfo(zoneInfo);
            console.log('대피구역 업데이트 완료:', zoneInfo);
        }
        console.log('중심점 이동 완료, 새로운 mapCenter:', [latlng.lat, latlng.lng]);
    };

    // 중심점 삭제 함수 추가
    const handleDeleteCenterPoint = () => {
        setSelectedAddress(null);
        setCenterAddress('');
        setMarkers([]);
        setEvacuationZones([]);
        setCalculatedZoneInfo(null);
        setWeatherData(null);
        // 중심점이 사라지면 입력값은 그대로 두고, 지도상에서만 제거
    };

    // 지도에 중복 표시할 데이터 id 배열 상태 추가
    // 데이터 목록에서 클릭 시 토글
    // 사용하지 않는 변수
    // 사용하지 않는 함수

    // URL 공유 함수
    const handleShareUrl = () => {
        const params = new URLSearchParams();
        if (selectedMaterial) params.set('material', selectedMaterial.id);
        if (amount) params.set('amount', amount);
        if (selectedUnit) params.set('unit', selectedUnit);
        if (mapCenter) {
            params.set('lat', mapCenter[0].toString());
            params.set('lon', mapCenter[1].toString());
        }
        const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        navigator.clipboard.writeText(url);
        alert('공유 URL이 클립보드에 복사되었습니다!');
    };

    // URL 파라미터로 상태 복원
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const materialId = params.get('material');
        const amountParam = params.get('amount');
        const unitParam = params.get('unit');
        const lat = params.get('lat');
        const lon = params.get('lon');
        if (materialId) {
            const mat = radioactiveMaterials.find(m => m.id === materialId);
            if (mat) setSelectedMaterial(mat);
        }
        if (amountParam) setAmount(amountParam);
        if (unitParam) setSelectedUnit(unitParam);
        if (lat && lon) setMapCenter([parseFloat(lat), parseFloat(lon)]);
    }, []);

    // centerAddress가 없고 위도/경도만 있을 때 reverse geocoding으로 한글 주소를 가져와서 표시
    useEffect(() => {
        if (!centerAddress && mapCenter) {
            fetchAddressFromCoords(mapCenter[0], mapCenter[1]).then(addr => {
                setCenterAddress(addr);
            });
        }
        // eslint-disable-next-line
    }, [mapCenter]);

    return (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100vh', background: '#B0B0B0' }}>
            {/* 모바일 AppBar + Drawer */}
            {isMobile && (
                <>
                    <AppBar position="static" sx={{ bgcolor: '#1C1C47' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
                                방사능 테러 대피거리 계산기
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                        <Box sx={{ width: 260, p: 2, bgcolor: '#1C1C47', height: '100%', color: '#fff' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>데이터 목록</Typography>
                            {dataList.map((item, index) => (
                                <Accordion
                                    key={item.id}
                                    expanded={expandedItemId === item.id}
                                    onChange={handleAccordionChange(item.id)}
                                    sx={{ mb: 1, bgcolor: '#1976D2', color: '#fff' }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                                        aria-controls={`panel${item.id}-content`}
                                        id={`panel${item.id}-header`}
                                    >
                                        <Typography>데이터 {index + 1}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ bgcolor: '#1565C0' }}>
                                        <Typography variant="body2" sx={{ color: '#fff' }}>
                                            물질: {item.물질}<br />
                                            물질량: {item.물질량} {item.unit}<br />
                                            주소: {item.주소}<br />
                                            {item.zoneInfo && (
                                                <>
                                                    <br />대피 반경:<br />
                                                    {item.zoneInfo.hot && (
                                                        <>
                                                            Hot Zone: 반경: 약 {item.zoneInfo.hot.radius.toFixed(1)} m<br />
                                                        </>
                                                    )}
                                                    {item.zoneInfo.warm && (
                                                        <>
                                                            Warm Zone: 반경: 약 {item.zoneInfo.warm.radius.toFixed(1)} m<br />
                                                        </>
                                                    )}
                                                    {item.zoneInfo.cold && (
                                                        <>
                                                            Cold Zone: 반경: 약 {item.zoneInfo.cold.radius.toFixed(1)} m<br />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {item.weather && (
                                                <>
                                                    <br />날씨:<br />
                                                    온도: {item.weather.temp.toFixed(1)}°C<br />
                                                    습도: {item.weather.humidity}%<br />
                                                    풍속: {item.weather.windSpeed.toFixed(1)} m/s<br />
                                                    풍향: {getWindDirectionText(item.weather.windDirection)}<br />
                                                    구름: {item.weather.cloudiness}%<br />
                                                    대기안정도(추정): {item.weather.stabilityClass}
                                                </>
                                            )}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </Drawer>
                </>
            )}
            {/* 데스크탑 사이드바 */}
            {!isMobile && (
                <Box sx={{ width: 320, background: '#1C1C47', color: '#fff', p: 2, overflowY: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>데이터 목록</Typography>
                    {dataList.map((item, index) => (
                        <Accordion
                            key={item.id}
                            expanded={expandedItemId === item.id}
                            onChange={handleAccordionChange(item.id)}
                            sx={{ mb: 1, bgcolor: '#1976D2', color: '#fff' }}
                            onClick={e => e.stopPropagation()}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                                aria-controls={`panel${item.id}-content`}
                                id={`panel${item.id}-header`}
                            >
                                <Typography>데이터 {index + 1}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: '#1565C0' }}>
                                <Typography variant="body2" sx={{ color: '#fff' }}>
                                    물질: {item.물질}<br />
                                    물질량: {item.물질량} {item.unit}<br />
                                    주소: {item.주소}<br />
                                    {item.zoneInfo && (
                                        <>
                                            <br />대피 반경:<br />
                                            {item.zoneInfo.hot && (
                                                <>
                                                    Hot Zone: 반경: 약 {item.zoneInfo.hot.radius.toFixed(1)} m<br />
                                                </>
                                            )}
                                            {item.zoneInfo.warm && (
                                                <>
                                                    Warm Zone: 반경: 약 {item.zoneInfo.warm.radius.toFixed(1)} m<br />
                                                </>
                                            )}
                                            {item.zoneInfo.cold && (
                                                <>
                                                    Cold Zone: 반경: 약 {item.zoneInfo.cold.radius.toFixed(1)} m<br />
                                                </>
                                            )}
                                        </>
                                    )}
                                    {item.weather && (
                                        <>
                                            <br />날씨:<br />
                                            온도: {item.weather.temp.toFixed(1)}°C<br />
                                            습도: {item.weather.humidity}%<br />
                                            풍속: {item.weather.windSpeed.toFixed(1)} m/s<br />
                                            풍향: {getWindDirectionText(item.weather.windDirection)}<br />
                                            구름: {item.weather.cloudiness}%<br />
                                            대기안정도(추정): {item.weather.stabilityClass}
                                        </>
                                    )}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
            {/* 메인 컨텐츠 */}
            <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ py: isMobile ? 1 : 4, flex: 1, background: '#B0B0B0', minWidth: 0 }}>
                {!isMobile && (
                    <Typography variant="h4" component="h1" gutterBottom align="center">
                        방사능 테러 대피거리 계산기
                    </Typography>
                )}
                <Paper elevation={3} sx={{ p: isMobile ? 1.5 : 3, mb: isMobile ? 2 : 3, background: '#FFFFFF' }}>
                    <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? 'small' : 'medium'}>
                        <InputLabel>방사성 물질 선택</InputLabel>
                        <Select
                            value={selectedMaterial?.id || ''}
                            onChange={handleMaterialChange}
                            label="방사성 물질 선택"
                        >
                            {radioactiveMaterials.map((material) => (
                                <MenuItem key={material.id} value={material.id}>
                                    {material.name} ({material.category})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        sx={{ mb: 2 }}
                        id="amount"
                        type="number"
                        value={amount}
                        onChange={handleAmountChange}
                        label="방사성 물질의 양"
                        size={isMobile ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Select
                                        value={selectedUnit}
                                        onChange={handleUnitChange}
                                        sx={{ height: isMobile ? '32px' : '40px' }}
                                        size={isMobile ? 'small' : 'medium'}
                                    >
                                        <MenuItem value="Bq">Bq</MenuItem>
                                        <MenuItem value="MBq">MBq</MenuItem>
                                        <MenuItem value="GBq">GBq</MenuItem>
                                        <MenuItem value="Ci">Ci</MenuItem>
                                    </Select>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 2 }}>
                        <TextField
                            label="주소나 장소를 입력하세요"
                            value={address}
                            onChange={e => setAddress(e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e)}
                            fullWidth
                            size={isMobile ? 'small' : 'medium'}
                            sx={{ flexGrow: 1 }}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleSearchAddress}
                            disabled={isSearching}
                            fullWidth={isMobile}
                            size={isMobile ? 'small' : 'medium'}
                        >
                            {isSearching ? '검색 중...' : '검색'}
                        </Button>
                    </Box>
                    {searchResults.length > 1 && (
                        <Autocomplete
                            freeSolo
                            options={searchResults}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.display_name)}
                            onChange={(_, newValue) => {
                                if (newValue) {
                                    handleAddressSelect(newValue as AddressResult);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="검색 결과"
                                    margin="normal"
                                    fullWidth
                                    size={isMobile ? 'small' : 'medium'}
                                />
                            )}
                            sx={{ mb: 2 }}
                        />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleCurrentLocation}
                            sx={{ flexGrow: 1, bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                        >
                            현재 위치
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleAddData}
                            sx={{ flexGrow: 1, bgcolor: '#007BFF', '&:hover': { bgcolor: '#0056b3' } }}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                        >
                            데이터 저장
                        </Button>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => setIsAddingCustomPoint(true)}
                        fullWidth
                        size={isMobile ? 'large' : 'medium'}
                        sx={{ mb: 1 }}
                    >
                        사용자 지정 지점 추가
                    </Button>
                    {selectedMaterial && selectedMaterial.physicalForm.includes('고체') && (
                        <Box sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={isExplosion} onChange={e => setIsExplosion(e.target.checked)} />}
                                label="폭발상황 (파편/분산 모델 적용)"
                            />
                        </Box>
                    )}
                </Paper>

                {/* 사용자 지정 지점 추가 다이얼로그 */}
                <Dialog open={isAddingCustomPoint && newCustomPointLocation !== null} onClose={() => {
                    setIsAddingCustomPoint(false);
                    setNewCustomPointLocation(null);
                    setNewCustomPointName('');
                    setNewCustomPointType('지휘소');
                }}>
                    <DialogTitle>사용자 지정 지점 추가</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            위도: {newCustomPointLocation?.length ? newCustomPointLocation[0].toFixed(4) : '-'},
                            경도: {newCustomPointLocation?.length ? newCustomPointLocation[1].toFixed(4) : '-'}
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="지점 이름"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newCustomPointName}
                            onChange={(e) => setNewCustomPointName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>지점 유형</InputLabel>
                            <Select
                                value={newCustomPointType}
                                onChange={(e) => setNewCustomPointType(e.target.value as string)}
                                label="지점 유형"
                            >
                                <MenuItem value="지휘소">지휘소</MenuItem>
                                <MenuItem value="제독소">제독소</MenuItem>
                                <MenuItem value="대피소">대피소</MenuItem>
                                <MenuItem value="병원">병원</MenuItem>
                                <MenuItem value="기타">기타</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsAddingCustomPoint(false);
                            setNewCustomPointLocation(null);
                            setNewCustomPointName('');
                            setNewCustomPointType('지휘소');
                        }}>취소</Button>
                        <Button onClick={async () => {
                            if (newCustomPointLocation && newCustomPointName) {
                                const [lat, lon] = newCustomPointLocation;
                                const address = await fetchAddressFromCoords(lat, lon);
                                setUserPoints(prev => [
                                    ...prev,
                                    {
                                        id: Date.now(),
                                        position: [lat, lon],
                                        label: newCustomPointName,
                                        type: 'custom',
                                        name: newCustomPointName,
                                        address,
                                        zones: null,
                                        zoneInfo: null,
                                    }
                                ]);
                                setIsAddingCustomPoint(false);
                                setNewCustomPointLocation(null);
                                setNewCustomPointName('');
                                setNewCustomPointType('지휘소');
                            } else {
                                alert('지점 이름과 위치를 입력해주세요.');
                            }
                        }}>추가</Button>
                    </DialogActions>
                </Dialog>

                {/* 선택된 방사성 물질 정보 */}
                <Accordion expanded={expandedMaterialInfo} onChange={handleMaterialInfoAccordionChange} sx={{ mb: 3 }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h6">방사성 물질 정보</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {selectedMaterial ? (
                            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                                <Table size="small" aria-label="material info table">
                                    <TableBody>
                                        {/* <TableRow>
                                            <TableCell component="th" scope="row" sx={{ width: '35%', fontWeight: 'bold' }}>이름</TableCell>
                                            <TableCell>{selectedMaterial.name}</TableCell>
                                        </TableRow> */}
                                        {/* 원자 번호, 질량 번호, 초기 방사능 행 삭제 */}
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>반감기</TableCell>
                                            <TableCell>{selectedMaterial.halfLife} ({selectedMaterial.halfLifeYears.toFixed(2)} 년)</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>감마 상수 (μSv·m²/MBq·h)</TableCell>
                                            <TableCell>{selectedMaterial.gammaConstant}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>카테고리</TableCell>
                                            <TableCell>{selectedMaterial.category}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>방사선 유형</TableCell>
                                            <TableCell>{selectedMaterial.radiationType.join(', ')}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>주요 용도</TableCell>
                                            <TableCell>{selectedMaterial.usages.join(', ')}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>위험 수준</TableCell>
                                            <TableCell>{selectedMaterial.dangerLevel} / 5</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>물리적 형태</TableCell>
                                            <TableCell>
                                                {selectedMaterial.physicalForm}
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    (물리적 형태에 따라 확산 형태가 달라질 수 있습니다.)
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography>방사성 물질을 선택해주세요.</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* 날씨 정보 및 대기 안정도 아코디언 */}
                {selectedAddress && weatherData && (
                    <Accordion expanded={expandedWeatherInfo} onChange={handleWeatherInfoAccordionChange} sx={{ mb: 3 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="weather-panel-content"
                            id="weather-panel-header"
                        >
                            <Typography variant="h6">날씨 및 대기 안정도</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flex: '0 0 calc(50% - 8px)' }}><Typography>온도: {weatherData.temp.toFixed(1)}°C</Typography></Box>
                                <Box sx={{ flex: '0 0 calc(50% - 8px)' }}><Typography>습도: {weatherData.humidity}%</Typography></Box>
                                <Box sx={{ flex: '0 0 calc(50% - 8px)' }}><Typography>풍속: {weatherData.windSpeed.toFixed(1)} m/s</Typography></Box>
                                <Box sx={{ flex: '0 0 calc(50% - 8px)' }}><Typography>풍향: {getWindDirectionText(weatherData.windDirection)}</Typography></Box>
                                <Box sx={{ flex: '0 0 100%' }}><Typography>구름: {weatherData.cloudiness}%</Typography></Box>
                                <Box sx={{ flex: '0 0 100%' }}><Typography>대기 안정도 (추정): {weatherData.stabilityClass}</Typography></Box>
                                <Box sx={{ flex: '0 0 100%' }}><Typography>날씨: {weatherData.description}</Typography></Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* 공유/이미지 버튼 영역 */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="primary" onClick={handleShareUrl} size={isMobile ? 'small' : 'medium'}>
                        공유 URL 복사
                    </Button>
                </Box>
                {/* 공유 캡처 영역 시작 */}
                <div ref={shareRef}>
                    {/* 현재 위치 주소 표시 */}
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F5F5F5', borderRadius: 1, display: 'flex', alignItems: 'center', minHeight: 40 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>현재 위치:</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {centerAddress}
                        </Typography>
                        {centerAddress && (
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                                <Button 
                                    size="small" 
                                    variant="outlined" 
                                    onClick={() => setIsMovingCenter(true)}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    중심점 이동
                                </Button>
                                <Button 
                                    size="small" 
                                    variant="outlined" 
                                    color="error" 
                                    onClick={handleDeleteCenterPoint}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    삭제
                                </Button>
                            </Box>
                        )}
                    </Box>
                    {/* 중심점 이동 모드 안내 */}
                    {isMovingCenter && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: '#856404', textAlign: 'center' }}>
                                🎯 지도를 클릭하여 중심점을 이동할 수 있습니다. 이동을 완료하려면 "이동 완료" 버튼을 클릭하세요.
                            </Typography>
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    size="small"
                                    onClick={() => setIsMovingCenter(false)}
                                >
                                    이동 완료
                                </Button>
                            </Box>
                        </Box>
                    )}
                    
                    {/* 기존 지도+결과+위험구역 정보 등 렌더링 부분 전체를 이 div로 감쌈 */}
                    <Box sx={{ height: 400, width: '100%', mb: 3 }}>
                        <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                            <ChangeView center={mapCenter} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            {/* 중심점 마커 제거 - 파란색 마커만 표시 */}
                            {markers.map((marker, index) => (
                                <Marker key={index} position={marker.position}>
                                    <Popup>{marker.name}</Popup>
                                </Marker>
                            ))}
                            {selectedMaterial && amount && selectedUnit && evacuationZones.map((zone, index) => (
                                <Circle
                                    key={`${zone.label}-${mapCenter[0].toFixed(6)}-${mapCenter[1].toFixed(6)}`}
                                    center={mapCenter}
                                    radius={zone.radius}
                                    pathOptions={{
                                        color: zone.label === 'Hot Zone' ? 'rgb(214, 55, 49)'
                                            : zone.label === 'Warm Zone' ? 'rgb(255, 255, 102)'
                                            : zone.label === 'Cold Zone' ? 'rgb(55, 175, 109)'
                                            : zone.label === 'Fragment Zone' ? '#FF9800'
                                            : 'rgb(55, 175, 109)',
                                        fillColor: zone.label === 'Hot Zone' ? 'rgb(214, 55, 49)'
                                            : zone.label === 'Warm Zone' ? 'rgb(255, 255, 102)'
                                            : zone.label === 'Cold Zone' ? 'rgb(55, 175, 109)'
                                            : zone.label === 'Fragment Zone' ? '#FF9800'
                                            : 'rgb(55, 175, 109)',
                                        opacity: 1,
                                        fillOpacity: zone.label === 'Hot Zone' ? 0.2
                                            : zone.label === 'Warm Zone' ? 0.25
                                            : zone.label === 'Cold Zone' ? 0.5
                                            : zone.label === 'Fragment Zone' ? 0.3
                                            : 0.5,
                                        interactive: false
                                    }}
                                >
                                    <Popup>
                                        {zone.label}: {zone.radius.toFixed(1)} m<br/>
                                        기준 선량률: {zone.doseRateThreshold} μSv/h
                                    </Popup>
                                </Circle>
                            ))}
                            {userPoints.map((point, idx) => (
                                <Marker
                                    key={point.id}
                                    position={point.position}
                                    draggable={true}
                                    icon={blueIcon}
                                    eventHandlers={{
                                        dragend: (e) => handleMarkerDragEnd(point.id, e.target.getLatLng())
                                    }}
                                >
                                    <Popup>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {point.label}
                                            </Typography>
                                            {point.address && (
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                                                    {point.address}
                                                </Typography>
                                            )}
                                            <Button 
                                                size="small" 
                                                variant="outlined" 
                                                color="error" 
                                                onClick={() => handleDeletePoint(point.id)}
                                                fullWidth
                                            >
                                                삭제
                                            </Button>
                                        </Box>
                                    </Popup>
                                </Marker>
                            ))}
                            <MapClickHandler 
                                onMapClickForCustomPoint={handleMapClickForCustomPoint} 
                                isAddingCustomPointMode={isAddingCustomPoint} 
                                onCenterMove={handleCenterMoveByClick}
                                isMovingCenterMode={isMovingCenter}
                                onMoveCenter={handleMoveCenter}
                            />
                        </MapContainer>
                    </Box>
                    {/* 위험 구역 정보 */}
                    {selectedMaterial && amount && selectedUnit && calculatedZoneInfo ? (
                        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>위험 구역 정보</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* 폭발상황(파편/분산)일 때 Fragment Zone 표시 */}
                                {calculatedZoneInfo.fragment && (
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ p: 1, bgcolor: '#FFE0B2', borderRadius: '4px', width: '100%' }}>
                                            <Typography variant="body1" sx={{ color: '#FF9800', fontWeight: 'bold' }}>Fragment Zone</Typography>
                                            <Typography variant="body2">
                                                반경: 약 {calculatedZoneInfo.fragment.radius < 0.1 ? `${(calculatedZoneInfo.fragment.radius * 100).toFixed(1)} cm` : `${calculatedZoneInfo.fragment.radius.toFixed(1)} m`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {/* 기존 Hot/Warm/Cold Zone 표시 */}
                                {calculatedZoneInfo.hot && (
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ p: 1, bgcolor: '#FFCDD2', borderRadius: '4px', width: '100%' }}>
                                            <Typography variant="body1" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>Hot Zone</Typography>
                                            <Typography variant="body2">
                                                반경: 약 {calculatedZoneInfo.hot.radius < 0.1 ? `${(calculatedZoneInfo.hot.radius * 100).toFixed(1)} cm` : `${calculatedZoneInfo.hot.radius.toFixed(1)} m`}<br/>
                                                ({20} μSv/h 이상)
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {calculatedZoneInfo.warm && (
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ p: 1, bgcolor: '#FFECB3', borderRadius: '4px', width: '100%' }}>
                                            <Typography variant="body1" sx={{ color: '#E65100', fontWeight: 'bold' }}>Warm Zone</Typography>
                                            <Typography variant="body2">
                                                반경: 약 {calculatedZoneInfo.warm.radius < 0.1 ? `${(calculatedZoneInfo.warm.radius * 100).toFixed(1)} cm` : `${calculatedZoneInfo.warm.radius.toFixed(1)} m`}<br/>
                                                ({0.2} μSv/h 이상)
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {calculatedZoneInfo.cold && (
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ p: 1, bgcolor: '#DCEDC8', borderRadius: '4px', width: '100%' }}>
                                            <Typography variant="body1" sx={{ color: '#33691E', fontWeight: 'bold' }}>Cold Zone</Typography>
                                            <Typography variant="body2">
                                                반경: 약 {calculatedZoneInfo.cold.radius < 0.1 ? `${(calculatedZoneInfo.cold.radius * 100).toFixed(1)} cm` : `${calculatedZoneInfo.cold.radius.toFixed(1)} m`}<br/>
                                                (0.001 μSv/h 이상)
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                방사성 물질, 양, 단위를 모두 입력하면 대피거리 정보가 표시됩니다.
                            </Typography>
                        </Paper>
                    )}
                </div>
                {/* 공유 캡처 영역 끝 */}
            </Container>
        </Box>
    );
}

export default App; 